import { useEffect, useId, useRef, useState } from 'react';
import { SketchPicker } from 'react-color';
import OutsideClickHandler from 'react-outside-click-handler';

import type { InputProps } from '../Input';

import styles from './_index.module.scss';

export interface InputColorProps {
  label: InputProps['label'];
  onChange?: (color: string) => void;
  value?: string;
}

const InputColor = ({ label, onChange = () => {}, value = '#00e36b' }: InputColorProps) => {
  const [color, setColor] = useState(value);
  const [isActive, setIsActive] = useState(false);
  const dialogId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const controlLabel = label ? `${label}${/\bcolor\b/i.test(label) ? '' : ' color'}` : 'Color';

  useEffect(() => {
    setColor((currentColor) => (currentColor === value ? currentColor : value));
  }, [value]);

  useEffect(() => {
    if (isActive) {
      dialogRef.current?.querySelector('input')?.focus();
    }
  }, [isActive]);

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (isActive && event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) {
      setIsActive(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && isActive) {
      event.preventDefault();
      event.stopPropagation();
      setIsActive(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div className={styles['input-color-wrapper']} onBlur={handleBlur} onKeyDown={handleKeyDown}>
      <OutsideClickHandler onOutsideClick={() => setIsActive(false)}>
        {label && <span className={styles['input-color-label']}>{label}</span>}
        <button
          aria-controls={dialogId}
          aria-expanded={isActive}
          aria-haspopup="dialog"
          aria-label={`${controlLabel}, ${color}`}
          className={styles['input-color-input-wrapper']}
          onClick={() => setIsActive(!isActive)}
          ref={triggerRef}
          type="button"
        >
          <span aria-hidden="true" className={styles['input-color-swatch']}>
            <span style={{ backgroundColor: color }} />
          </span>
          <span>{color}</span>
        </button>
        {isActive && (
          <div aria-label={`${controlLabel} picker`} id={dialogId} ref={dialogRef} role="dialog">
            <SketchPicker
              color={color}
              disableAlpha
              onChange={(updatedColor) => {
                setColor(updatedColor.hex);
                onChange(updatedColor.hex);
              }}
              presetColors={[]}
            />
          </div>
        )}
      </OutsideClickHandler>
    </div>
  );
};

export default InputColor;
